import { Currency } from '@brunommsilva/react-foundations';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import useTransactionsSummary from '../../hooks/transactions/use-transactions-summary';
import { TransactionType } from '../../models/transactions';
import getCurrencyColor from './currency-color';

/** The order of the figures after the balance. */
const TYPES_IN_ORDER = [
  TransactionType.CREDIT,
  TransactionType.DEBIT,
  TransactionType.TRANSFER,
];

interface FigureProps {
  label: string;
  amount: number;
  color: ReturnType<typeof getCurrencyColor>;
}

/** An amount with nothing to show for it takes the colour of the text around it, which is muted. */
const Figure = ({ label, amount, color }: FigureProps) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography
      variant="subtitle1"
      sx={{
        lineHeight: 1.3,
        color: color === 'inherit' ? 'text.secondary' : undefined,
      }}
    >
      <Currency amount={amount} color={color} />
    </Typography>
  </Box>
);

interface TransactionsSummaryProps {
  /** The filter of the list: the summary is of all that matches it, and not only of the page of the list. */
  filter: object;
}

/** How many movements match the filter and what they add up to, in a block above the list. */
const TransactionsSummary = ({ filter }: TransactionsSummaryProps) => {
  const { t } = useTranslation();
  const { summary, isLoading, hasError } = useTransactionsSummary(filter);

  if (isLoading) {
    return <Skeleton variant="rounded" height={72} />;
  }

  // The list says that it failed, and a summary of nothing would be a wrong one.
  if (hasError) {
    return null;
  }

  const amountOf = (type: TransactionType) =>
    summary.byTransactionType.find(
      ({ transactionType }) => transactionType === type
    )?.amount ?? 0;
  // What came in minus what went out; the transfers move money between accounts, so they leave it as it is.
  const balance =
    Math.round(
      (amountOf(TransactionType.CREDIT) - amountOf(TransactionType.DEBIT)) * 100
    ) / 100;
  const balanceColor =
    balance > 0 ? 'success' : balance < 0 ? 'error' : 'inherit';

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'action.hover',
        px: 2,
        py: 1.5,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {t('TRANSACTIONS_SUMMARY_COUNT', { count: summary.transactionsCount })}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 1.5,
          mt: 0.5,
        }}
      >
        <Figure label={t('NET_RESULT')} amount={balance} color={balanceColor} />
        {TYPES_IN_ORDER.map((type) => (
          <Figure
            key={type}
            label={t(`TRANSACTIONS_SUMMARY_${type}`)}
            amount={amountOf(type)}
            color={amountOf(type) > 0 ? getCurrencyColor(type) : 'inherit'}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TransactionsSummary;
